﻿import {
  FLORIDA_LOTTERY_SCHEDULE,
  FLORIDA_RESULTS_SYNC_OFFSET_MINUTES,
  RESULTS_WAIT_BLOCK_OFFSETS,
  RESULTS_WAIT_REOPEN_MINUTES,
} from '../lottery/floridaSchedule';
import {
  NEW_YORK_LOTTERY_SCHEDULE,
} from '../lottery/newYorkSchedule';
import { easternHHMMToCubaHHMM } from '../time/easternToCuba';

export type LotterySourceId =
  | 'florida'
  | 'georgia'
  | 'newyork'
  | 'classic'
  | 'chicago'
  | 'custom'
  | string;

/** Results-wait block in Cuba civil time (no play acceptance). */
export interface ResultsWaitBlock {
  from: string;
  to: string;
}

export interface SourcePlayWindows {
  dayOpen: string;
  dayClose: string;
  nightOpen: string;
  nightClose: string;
  dayBlock: ResultsWaitBlock;
  nightBlock: ResultsWaitBlock;
  /** Length of the day wait block in minutes (legacy / config hints). */
  resultsGapMinutes: number;
  /** Minutes after block end before next turn opens. */
  reopenAfterBlockMinutes: number;
}

/**
 * Auto-sync still runs at draw + 10 min (cron). Play acceptance uses longer
 * results-wait blocks derived from the official Eastern draw clock.
 */
export const INTER_TURN_GAP_MINUTES = FLORIDA_RESULTS_SYNC_OFFSET_MINUTES;

export { RESULTS_WAIT_REOPEN_MINUTES };

/** Sources that publish an official ET draw schedule we can map into Cuba windows. */
export function sourceHasOfficialWindows(source: LotterySourceId): boolean {
  return source === 'florida' || source === 'newyork';
}

function parseHHMMToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

function addMinutesToHHMM(value: string, deltaMinutes: number): string {
  const total = (parseHHMMToMinutes(value) + deltaMinutes + 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Derive acceptance open/close from results-wait blocks (+ reopen delay). */
export function acceptanceFromResultsBlocks(
  dayBlock: ResultsWaitBlock,
  nightBlock: ResultsWaitBlock,
  reopenAfterBlockMinutes: number = RESULTS_WAIT_REOPEN_MINUTES,
): Pick<SourcePlayWindows, 'dayOpen' | 'dayClose' | 'nightOpen' | 'nightClose'> {
  return {
    dayClose: dayBlock.from,
    nightOpen: addMinutesToHHMM(dayBlock.to, reopenAfterBlockMinutes),
    nightClose: nightBlock.from,
    dayOpen: addMinutesToHHMM(nightBlock.to, reopenAfterBlockMinutes),
  };
}

/** Inverse: wait blocks implied by stored acceptance open/close (+ reopen). */
export function resultsBlocksFromAcceptance(
  dayOpen: string,
  dayClose: string,
  nightOpen: string,
  nightClose: string,
  reopenAfterBlockMinutes: number = RESULTS_WAIT_REOPEN_MINUTES,
): { dayBlock: ResultsWaitBlock; nightBlock: ResultsWaitBlock } {
  return {
    dayBlock: {
      from: dayClose,
      to: addMinutesToHHMM(nightOpen, -reopenAfterBlockMinutes),
    },
    nightBlock: {
      from: nightClose,
      to: addMinutesToHHMM(dayOpen, -reopenAfterBlockMinutes),
    },
  };
}

function blockDurationMinutes(from: string, to: string): number {
  return (parseHHMMToMinutes(to) - parseHHMMToMinutes(from) + 24 * 60) % (24 * 60);
}

/**
 * Build Cuba play windows from Eastern draw times + wait-block offsets.
 * Day often spans midnight (evening block end → next midday block start).
 */
function etDrawsToCubaWindows(
  middayDraw: string,
  eveningDraw: string,
  nowUtc: Date,
): SourcePlayWindows {
  const dayBlockEtFrom = addMinutesToHHMM(
    middayDraw,
    RESULTS_WAIT_BLOCK_OFFSETS.dayStartFromDrawMin,
  );
  const dayBlockEtTo = addMinutesToHHMM(
    middayDraw,
    RESULTS_WAIT_BLOCK_OFFSETS.dayEndFromDrawMin,
  );
  const nightBlockEtFrom = addMinutesToHHMM(
    eveningDraw,
    RESULTS_WAIT_BLOCK_OFFSETS.nightStartFromDrawMin,
  );
  const nightBlockEtTo = addMinutesToHHMM(
    eveningDraw,
    RESULTS_WAIT_BLOCK_OFFSETS.nightEndFromDrawMin,
  );

  const dayBlock: ResultsWaitBlock = {
    from: easternHHMMToCubaHHMM(dayBlockEtFrom, nowUtc),
    to: easternHHMMToCubaHHMM(dayBlockEtTo, nowUtc),
  };
  const nightBlock: ResultsWaitBlock = {
    from: easternHHMMToCubaHHMM(nightBlockEtFrom, nowUtc),
    to: easternHHMMToCubaHHMM(nightBlockEtTo, nowUtc),
  };

  const acceptance = acceptanceFromResultsBlocks(
    dayBlock,
    nightBlock,
    RESULTS_WAIT_REOPEN_MINUTES,
  );

  return {
    ...acceptance,
    dayBlock,
    nightBlock,
    resultsGapMinutes: blockDurationMinutes(dayBlock.from, dayBlock.to),
    reopenAfterBlockMinutes: RESULTS_WAIT_REOPEN_MINUTES,
  };
}

/**
 * Default play windows in Cuba civil time for a lottery source.
 * Florida/NY: official Eastern draws → results-wait blocks → acceptance.
 * Manual sources return null — banker keeps or edits windows.
 */
export function windowsForLotterySource(
  source: LotterySourceId,
  nowUtc: Date = new Date(),
): SourcePlayWindows | null {
  if (source === 'florida') {
    return etDrawsToCubaWindows(
      FLORIDA_LOTTERY_SCHEDULE.middayDraw,
      FLORIDA_LOTTERY_SCHEDULE.eveningDraw,
      nowUtc,
    );
  }
  if (source === 'newyork') {
    return etDrawsToCubaWindows(
      NEW_YORK_LOTTERY_SCHEDULE.middayDraw,
      NEW_YORK_LOTTERY_SCHEDULE.eveningDraw,
      nowUtc,
    );
  }
  return null;
}

/** Merge official source windows into a config draft (no-op for manual sources). */
export function applySourceWindowDefaults<T extends Record<string, unknown>>(
  draft: T,
  source: LotterySourceId,
  nowUtc: Date = new Date(),
): T {
  const windows = windowsForLotterySource(source, nowUtc);
  if (!windows) return { ...draft, source } as T;
  return {
    ...draft,
    source,
    dayOpen: windows.dayOpen,
    dayClose: windows.dayClose,
    nightOpen: windows.nightOpen,
    nightClose: windows.nightClose,
  } as T;
}
